import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import { FilterLink, AccordionFilter } from '@/components/molecules';
import { UNLIMITED_DRAWER } from '@/utils/Filters';
import { getLabel } from '@/utils/index';
import { ICategoriesListBrandCmp } from './ICategoriesListBrandCmp';
import { ICollectionTagFields } from '@/types/contentful/contentful';
import { FacetValue } from '@/lib/models/types/product';

interface StyleProps {
  noMaxHeight?: boolean;
  bulletLink?: boolean;
}

const Content = styled.div`
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
`;

const AccordionContentWrapper = styled.div<StyleProps>`
  padding: 0 2px;
  max-height: ${props => (props.noMaxHeight ? 'none' : '262px')};
  overflow: auto;
`;

const FilterLinkWrapper = styled.div<StyleProps>`
  width: 100%;
  margin-bottom: ${props => (!props.bulletLink ? '4px' : '0')};
`;
const FilterLinkWrapperNew = styled.div<StyleProps>`
  width: 100%;
  margin-bottom: ${props => (!props.bulletLink ? '4px' : '0')};
`;

const CategoriesListBrand: FC<ICategoriesListBrandCmp> = ({
  categoryPath,
  slug,
  queryParameters,
  cmsCollectionTags,
}) => {
  const { t } = useTranslation(['catalog']);
  const [expanded, setExpanded] = useState<string[] | null>(null);

  useEffect(() => {
    if (slug && slug.length > 1) {
      setExpanded(slug.slice(1));
    } else {
      setExpanded(null);
    }
  }, [slug]);

  const toggleExpanded =
    (toggledCategoryPath: string, selectedCategoryPath: string) =>
    (_event: any, isExpanded: boolean) => {
      const toggledCategoryPathTokens = toggledCategoryPath.split('/');

      if (!isExpanded) {
        if (toggledCategoryPath !== slug[1]) {
          setExpanded([slug[1]]);
        } else {
          setExpanded([]);
        }
      } else {
        if (toggledCategoryPath === selectedCategoryPath) {
          setExpanded(toggledCategoryPathTokens);
        } else {
          setExpanded(toggledCategoryPathTokens);
        }
      }
    };

  let list;

  if (!slug || slug.length === 0) {
  } else {
    let accordionSections = [];
    if (slug && slug.length === 1) {
      accordionSections.push(
        <FilterLinkWrapper key={`wrapper-filter-all-${slug[0]}`}>
          <FilterLink
            key={`filter-all-${slug[0]}`}
            filterInfo={{
              filterKey: 'category-path',
              filter: {
                slug: `all-${slug[0]}`,
                label: t('catalog:all-products'),
                tag: '',
              },
              active: true,
            }}
            pathInfo={{
              basePath: '/brands/[slug]',
              slug: [slug[0]],
              queryParams: queryParameters,
              shallow: true,
            }}></FilterLink>
        </FilterLinkWrapper>
      );
    }
    if (categoryPath && cmsCollectionTags) {
      list = getBrowseAccordionCategories(
        accordionSections,
        categoryPath,
        slug as string[],
        queryParameters,
        cmsCollectionTags,
        slug && slug.length === 1 ? true : false,
        expanded,
        toggleExpanded
      );
    }
  }

  let content;
  if (slug && slug.length >= 2) {
    content = list;
  } else {
    let rootAccordion;

    if (slug.length === 1) {
      rootAccordion = {
        slug: `all-${slug[0]}`,
        heading: `${getLabel(slug[0], cmsCollectionTags.gender)} Collection`,
      };
    }

    content = (
      <AccordionFilter
        key={rootAccordion?.slug}
        heading={rootAccordion?.heading!}
        template="collectionFilters"
        defaultExpanded={true}
        indent={true}
        active={true}>
        <>{list}</>
      </AccordionFilter>
    );
  }

  return <Content>{content}</Content>;
};

export default CategoriesListBrand;

const getBrowseAccordionCategories = (
  accordionSections: JSX.Element[],
  categoryPathArray: FacetValue[],
  slug: string[],
  queryParameters: { [key: string]: string | string[] | undefined },
  cmsCollectionTags: { [key: string]: ICollectionTagFields[] },
  onlyLinks: boolean,
  expanded: string[] | null,
  toggleExpanded: (
    toggledCategoryPath: string,
    selectedCategoryPath: string
  ) => (_event: any, isExpanded: boolean) => void
): JSX.Element[] => {
  if (categoryPathArray && categoryPathArray.length > 0) {
    categoryPathArray.forEach((categoryPath, index) => {
      let isActiveCategoryPath: boolean = false;

      if (slug && slug.length > 0) {
        isActiveCategoryPath =
          slug.slice(1).join('/') === categoryPath.queryValue;
      } else {
        if (queryParameters['category-path']) {
          isActiveCategoryPath =
            queryParameters['category-path'] === categoryPath.queryValue;
        }
      }

      let splitCategoryPath = categoryPath?.queryValue.toLowerCase().split('/');

      let label = getLabel(
        categoryPath.queryValue,
        cmsCollectionTags[categoryPath.queryValue],
        categoryPath?.displayValue
      );

      let linkQueryParameters = {
        ...queryParameters,
        ...{
          'category-path': categoryPath.queryValue.replaceAll('/', ','),
        },
      };

      if ((!slug || slug.length === 0) && categoryPath?.queryValue !== '') {
        linkQueryParameters = {
          ...linkQueryParameters,
        };
      }
      let linkSlugArray: string[] = [];
      let catParameter = {};

      if (slug?.length > 0) {
        if (isActiveCategoryPath) {
          linkSlugArray = [slug[0]];
        } else {
          linkSlugArray = [slug[0]];
        }
      } else {
        if (isActiveCategoryPath) {
          splitCategoryPath = splitCategoryPath.slice(
            0,
            splitCategoryPath.length - 1
          );
        }
        let arrCatString = splitCategoryPath.join('/');

        if (arrCatString.length > 0) {
          catParameter = { 'category-path': arrCatString };
          linkQueryParameters = {
            ...linkQueryParameters,
            ...catParameter,
          };
        } else {
          // delete linkQueryParameters['category-path'];
        }
      }
      const isBulletLink =
        categoryPath?.queryValue.split('/').length >= 3 ||
        (categoryPath?.queryValue.split('/').length >= 2 &&
          categoryPath.values &&
          categoryPath.values.length > 0);

      let filterLink = (
        <FilterLinkWrapperNew
          key={`wrapper-filter-${categoryPath?.id}${index}`}
          bulletLink={isBulletLink}>
          <FilterLink
            key={`filter-${categoryPath?.id}${index}`}
            filterInfo={{
              filterKey: 'category-path',
              active: isActiveCategoryPath,
              filter: {
                slug: categoryPath?.queryValue,
                label:
                  slug &&
                  slug.length >= 2 &&
                  categoryPath.values &&
                  categoryPath.values.length > 0
                    ? 'All ' + label
                    : label,
                tag: '',
              },
            }}
            pathInfo={{
              basePath: '/brands/[slug]',
              slug: linkSlugArray,
              queryParams: linkQueryParameters,
              shallow: true,
            }}
            bulletLink={isBulletLink}></FilterLink>
        </FilterLinkWrapperNew>
      );

      if (categoryPath.values && categoryPath.values.length > 0 && !onlyLinks) {
        const accordion = (
          <FilterLinkWrapper key={`wrapperacc-${categoryPath?.id}${index}`}>
            <AccordionFilter
              key={`acc-${categoryPath?.id}${index}`}
              heading={getLabel(
                categoryPath?.queryValue,
                cmsCollectionTags[categoryPath?.queryValue],
                categoryPath?.displayValue
              )}
              template="collectionFilters"
              onChange={toggleExpanded(
                categoryPath?.queryValue,
                slug.slice(1).join('/')
              )}
              expanded={
                expanded && expanded.includes(categoryPath.id) ? true : false
              }
              indent={
                slug && slug[1] && slug[1] === categoryPath?.id ? true : false
              }
              active={
                (slug && slug[1] && slug[1] === categoryPath?.id) ||
                (slug && slug[2] && slug[2] === categoryPath?.id)
                  ? true
                  : false
              }>
              <AccordionContentWrapper
                className={'customWrapper'}
                noMaxHeight={UNLIMITED_DRAWER.includes(categoryPath?.id)}>
                {filterLink}
                {getBrowseAccordionCategories(
                  [],
                  categoryPath.values,
                  slug,
                  queryParameters,
                  cmsCollectionTags,
                  false,
                  expanded,
                  toggleExpanded
                )}
              </AccordionContentWrapper>
            </AccordionFilter>
          </FilterLinkWrapper>
        );
        accordionSections.push(accordion);
      } else {
        accordionSections.push(filterLink);
      }
    });
  }

  return accordionSections;
};
